import React, { useCallback, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Coords, ElementSize, Page } from 'editor/src/store/design/types';
import setSidebarActiveTabByNameOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabByNameOperation';
import hasSidebarTab from 'editor/src/store/editorModules/sidebar/selector/hasSidebarTab';
import { TAB_NAMES } from 'editor/src/store/editorModules/sidebar/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import { CanvasRotation } from 'editor/src/component/EditorArea/types';
import useEmptyPlaceholderEventHandlers from 'editor/src/component/EditorArea/useEmptyPlaceholderEventHandlers';
import { UploaderContext } from 'editor/src/component/Uploader/useUploader';

import EmptyPlaceholderIcon from './EmptyPlaceholderIcon';
import usePlaceholderPositioning from './usePlaceholderPositioning';

import styles from './EmptyContentPlaceholderDiscoverable.module.scss';

interface Props {
  spreadCoords: Coords;
  content: ElementSize;
  canvasRotation: CanvasRotation;
  page: Page;
}

function EmptyContentPlaceholderDiscoverable({ content, spreadCoords, canvasRotation, page }: Props) {
  const { placeholderContainerRef, placeholderSize, position, isDragging, isMobile, initialSizeVariant } =
    usePlaceholderPositioning(content, spreadCoords, canvasRotation, page);

  const uploader = useContext(UploaderContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { onPlaceholderClick } = useEmptyPlaceholderEventHandlers(uploader);
  const showBrowseGraphics = useSelector((state) => hasSidebarTab(state, TAB_NAMES.ADDONS));

  const handleFilesClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onPlaceholderClick(true);
    },
    [onPlaceholderClick],
  );

  const handleGraphicsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(setSidebarActiveTabByNameOperation(TAB_NAMES.ADDONS));
    },
    [dispatch],
  );

  const fullDescKey = `empty-design-content-placeholder-description${isMobile ? '-mobile' : ''}${showBrowseGraphics ? '-with-graphics' : ''}`;
  const shortDescKey = `empty-design-content-placeholder-description-short${isMobile ? '-mobile' : ''}`;

  return (
    <div
      className={styles.placeholderContainer}
      ref={placeholderContainerRef}
      data-size={initialSizeVariant}
      style={{
        width: `${placeholderSize}px`,
        height: `${placeholderSize}px`,
        transform: `translate3d(${position.left}px,${position.top}px,0) scale(${position.zoom})`,
        pointerEvents: isDragging ? 'none' : 'auto',
      }}
    >
      <div className={styles.area} onClick={handleFilesClick}>
        <div className={styles.iconContainer}>
          <EmptyPlaceholderIcon className={styles.icon} />
        </div>
        <div className={styles.textGroup}>
          <span className={styles.title}>{t('empty-design-content-placeholder-title')}</span>
          <span className={styles.descriptionFull}>
            <Trans
              i18nKey={fullDescKey}
              components={{
                filesLink: (
                  <button type="button" aria-label="files section" className={styles.link} onClick={handleFilesClick} />
                ),
                graphicsLink: (
                  <button
                    type="button"
                    aria-label="graphics library"
                    className={styles.link}
                    onClick={handleGraphicsClick}
                  />
                ),
              }}
            />
          </span>
          <span className={styles.descriptionShort}>{t(shortDescKey)}</span>
        </div>
      </div>
    </div>
  );
}

export default React.memo(EmptyContentPlaceholderDiscoverable);
